import React, {useMemo} from 'react';
import {GridColumnProps, GridIdNameModel, GridRowModel} from '../../../../../../components/shared/grid/types';
import {Lx} from '../../../../../../i18n/consts';
import {IntlShape} from 'react-intl';
import {
    TimeSheetGroupType,
    TimeSheetItem,
    TimeSheetItemStatus,
    TimeSheetItemType,
    UpdateStatusAbility
} from '../../../../../../features/schedule/types';
import _ from 'lodash';
import {AbsenceBehavior, TiminatorAbsenceTypes, TiminatorTypes} from '../../../../../../features/types/types';
import {PIdNameModel} from '../../../../../../features/projects/types';
import {Employee} from '../../../../../../features/employees/types';
import TimeSheetActionMenu from '../../../../../../components/Schedule/time-shift/components/action-menu';
import StateIcon from '../../../../../../components/Schedule/timesheet-state-icon';
import {
    ActionIconButton,
    HoursValue,
    TimeBankCellStripe,
    formatHours,
    getTimeBankCellTitle,
    getTimeBankLabel
} from '../../components/cells';
import styles from '../../grid.module.scss';
import moment, {Moment} from 'moment';

const renderSingleLineValue = (value: string): React.ReactNode => (
    <span className={styles.singleLineValue} title={value}>
        <span className={styles.singleLineValueText}>{value}</span>
    </span>
);

const renderTypeValue = (value: string): React.ReactNode => {
    return (
        <span className={styles.typeValue}>
            <span className={styles.typeValueText} title={value}>{value}</span>
        </span>
    );
};

const mapAbsenceTypeOptions = (
    item: TimeSheetItem,
    absenceTypes: TiminatorAbsenceTypes
): GridIdNameModel[] => {
    const options = _.chain(absenceTypes.items).orderBy((x) => x.default ? 0 : x.id).map((x) => ({
        id: x.id,
        name: x.name
    })).value();

    if (item.targetId > 0 && item.targetName && !options.some((option) => option.id === item.targetId)) {
        return [{ id: item.targetId, name: item.targetName }, ...options];
    }

    return options;
};

const useGridColumns = (
    groupType: TimeSheetGroupType,
    shiftTypes: TiminatorTypes,
    absenceTypes: TiminatorAbsenceTypes,
    projects: PIdNameModel[],
    onApproveClick: (item: TimeSheetItem, approve: boolean) => void,
    onBillClick: (item: TimeSheetItem, billable: boolean) => void,
    onAddShiftClick: (item: TimeSheetItem) => void,
    onEditItemClick: (item: TimeSheetItem) => Promise<void>,
    onCopyItemClick: (item: TimeSheetItem) => Promise<void>,
    getShiftEmployee: (item: TimeSheetItem) => Promise<Employee>,
    onDeleteItemClick: (item: TimeSheetItem) => void,
    approveAbility: UpdateStatusAbility,
    billalbeAbility: UpdateStatusAbility,
    canManageEntry: (item: TimeSheetItem) => boolean,
    canManageInternalNote: boolean,
    canViewInternalNote: boolean,
    includeTrackingColumns = true,
    includeApprovalColumns = true,
    disabled = false,
    compactDesktop = false,
    compactTextColumns = false,
    minEditableDate?: Moment,
    maxEditableDate?: Moment
): GridColumnProps[] => {
    const approvedHoursWidth = compactDesktop ? 88 : 100;
    const billableHoursWidth = compactDesktop ? 88 : 100;
    const totalColumnWidth = compactDesktop ? 68 : 80;
    const actionColumnWidth = compactDesktop ? 34 : 40;
    const dateColumnWidth = compactDesktop ? 120 : 120;
    const detailsColumnWidth = compactDesktop ? 140 : 160;
    const textDetailsColumnWidth = compactTextColumns ? 90 : detailsColumnWidth;
    const trackingColumnWidth = compactDesktop ? 72 : 80;

    const getMetrics = (item: TimeSheetItem) => {
        const workHours = item.timeshiftType === TimeSheetItemType.SHIFT ? item.hours : 0;
        const absenceHours = item.timeshiftType === TimeSheetItemType.ABSENCE
            && item.behavior !== AbsenceBehavior.WillWork
            ? item.hours
            : 0;
        const approvedHours = item.behavior !== AbsenceBehavior.WillWork
            && (item.status === TimeSheetItemStatus.APPROVED || item.status === TimeSheetItemStatus.BILLABLE)
            ? item.hours
            : 0;
        const billableHours = item.status === TimeSheetItemStatus.BILLABLE ? item.hours : 0;

        return { workHours, absenceHours, approvedHours, billableHours };
    };
    const canShowApproveAction = Boolean(approveAbility?.canApply || approveAbility?.canCancel);
    const canShowBillableAction = Boolean(billalbeAbility?.canApply || billalbeAbility?.canCancel);

    const buildApproveButton = (item: TimeSheetItem, intl: IntlShape, disabled?: boolean) => {
        const shouldApprove = item.status === TimeSheetItemStatus.NORMAL;
        const buttonDisabled = disabled
            || (approveAbility
                ? shouldApprove
                    ? !approveAbility.canApply
                    : !approveAbility.canCancel
                : true);
        const label = shouldApprove
            ? intl.formatMessage({ id: Lx.Schedule.APPROVE_FORMAT }, { s: formatHours(item.hours) })
            : intl.formatMessage({ id: Lx.Schedule.DISAPPROVE_FORMAT }, { s: formatHours(item.hours) });

        return (
            <ActionIconButton
                icon={
                    <StateIcon
                        status={shouldApprove ? TimeSheetItemStatus.NORMAL : TimeSheetItemStatus.APPROVED}
                        active={!shouldApprove}
                    />
                }
                label={label}
                disabled={buttonDisabled}
                onClick={() => onApproveClick(item, shouldApprove)}
                variant='approve'
                active={!shouldApprove}
            />
        );
    };

    const buildBillableButton = (item: TimeSheetItem, intl: IntlShape, disabled?: boolean) => {
        const isBillableShift = item.timeshiftType === TimeSheetItemType.SHIFT && item.isBillable;
        const shouldBill = item.status !== TimeSheetItemStatus.BILLABLE;
        const buttonDisabled = disabled
            || !isBillableShift
            || (billalbeAbility
                ? shouldBill
                    ? !billalbeAbility.canApply
                    : !billalbeAbility.canCancel
                : true);
        const label = shouldBill
            ? intl.formatMessage({ id: Lx.Schedule.BILL_SHIFT_FORMAT }, { s: formatHours(item.hours) })
            : intl.formatMessage({ id: Lx.Schedule.CANCEL_BILL_FORMAT }, { s: formatHours(item.hours) });

        return (
            <ActionIconButton
                icon={<StateIcon status={TimeSheetItemStatus.BILLABLE} active={!shouldBill} />}
                label={label}
                disabled={buttonDisabled}
                onClick={() => onBillClick(item, shouldBill)}
                variant='billable'
                active={!shouldBill}
            />
        );
    };

    const buildActionMenu = (item: TimeSheetItem, intl: IntlShape, disabled?: boolean) => {
        const shouldApprove = item.status === TimeSheetItemStatus.NORMAL;
        const canUseApproveAction = shouldApprove
            ? Boolean(approveAbility?.canApply)
            : Boolean(approveAbility?.canCancel);
        const approveDisabled = disabled || !canUseApproveAction;

        const isBillableShift = item.timeshiftType === TimeSheetItemType.SHIFT && item.isBillable;
        const shouldBill = item.status !== TimeSheetItemStatus.BILLABLE;
        const canUseBillableAction = isBillableShift && (shouldBill
            ? Boolean(billalbeAbility?.canApply)
            : Boolean(billalbeAbility?.canCancel));
        const billableDisabled = disabled || !canUseBillableAction;
        const hideBillable = !canUseBillableAction;
        const hideApprove = !canUseApproveAction;

        return (
            <TimeSheetActionMenu
                intl={intl}
                status={item.status}
                timeshiftType={item.timeshiftType}
                hours={item.hours}
                hideApprovable={hideApprove}
                hideBillable={hideBillable}
                disableBillable={billableDisabled}
                disableApprovable={approveDisabled}
                onApproveClick={() => onApproveClick(item, shouldApprove)}
                onBillableClick={() => onBillClick(item, shouldBill)}
                onAddNewShiftClick={() => onAddShiftClick(item)}
                onEditTimeClick={() => onEditItemClick(item)}
                onCopyItemClick={() => onCopyItemClick(item)}
                onDeleteItemClick={() => onDeleteItemClick(item)}
                disabled={disabled}
                hideAddAction={!canManageEntry(item)}
                hideEditAction={!canManageEntry(item)}
                hideCopyAction={!canManageEntry(item)}
                hideDeleteAction={!canManageEntry(item)}
            />
        );
    };
    const canEditRow = (row: GridRowModel) => !disabled && canManageEntry(row as TimeSheetItem);

    return useMemo((): GridColumnProps[] => {
        const showTrackingColumns = groupType === TimeSheetGroupType.EMPLOYEEVIEW;

        const columns: GridColumnProps[] = [
            {
                field: 'date',
                localizationKey: Lx.Schedule.TIMESHIFT_DATE,
                noHeader: true,
                type: 'date',
                editable: canEditRow,
                minDate: minEditableDate,
                maxDate: maxEditableDate,
                width: dateColumnWidth,
                alignment: 'center',
                displayValue: (value) => {
                    const date = moment(value, 'YYYY-MM-DD', true)
                    return <span>
                        <span className={styles.dateWeekday}>{date.format('dd')}</span>
                        <span>{date.format('L')}</span>
                    </span>
                }
            },
            {
                field: 'employeeName',
                localizationKey: Lx.General.EMPLOYEE,
                width: detailsColumnWidth,
                editable: false,
                alignment: 'start',
                visible: groupType === TimeSheetGroupType.PROJECTVIEW
            },
            {
                field: 'target',
                localizationKey: Lx.Schedule.TIMESHIFT_TARGET,
                width: textDetailsColumnWidth,
                cellClassName: styles.compactTextCell,
                editable: canEditRow,
                type: 'singleSelect',
                selectableOptions: async (row: GridRowModel): Promise<GridIdNameModel[]> => {
                    const timeshift = row as TimeSheetItem;
                    if (timeshift.timeshiftType !== TimeSheetItemType.SHIFT) {
                        return mapAbsenceTypeOptions(timeshift, absenceTypes);
                    }

                    return _.chain((await getShiftEmployee(timeshift)).projects).orderBy((p) => p.id).map((x) => ({
                        id: x.id,
                        name: x.name
                    })).value();
                },
                value: (row: GridRowModel) => (row as TimeSheetItem).targetId,
                displayValue: (value: number, row: GridRowModel) => {
                    const timeshift = row as TimeSheetItem;
                    const id = value;
                    if (timeshift.timeshiftType === TimeSheetItemType.SHIFT) {
                        return renderSingleLineValue(projects.find((x) => x.id === id)?.name ?? `Project [${id}]`);
                    }
                    return renderSingleLineValue(absenceTypes.items.find((x) => x.id === id)?.name ?? timeshift.targetName ?? `Absence [${id}]`);
                },
                visible: groupType === TimeSheetGroupType.EMPLOYEEVIEW
            },
            {
                field: 'type',
                localizationKey: Lx.Schedule.TIMESHIFT_TYPE,
                width: textDetailsColumnWidth,
                cellClassName: styles.compactTextCell,
                value: (row: GridRowModel) => (row as TimeSheetItem).timeshiftTypeId,
                displayValue: (value: number, row: GridRowModel) => {
                    const timeshift = row as TimeSheetItem;
                    if (timeshift.timeshiftType === TimeSheetItemType.SHIFT) {
                        const id = value;
                        return renderTypeValue(
                            shiftTypes.items.find((x) => x.id === id)?.name ?? `ShiftType [${id}]`
                        );
                    }
                    return renderTypeValue(
                        absenceTypes.items.find((x) => x.id === timeshift.timeshiftTypeId)?.name ?? timeshift.targetName ?? 'AbsenceType'
                    );
                },
                editable: canEditRow,
                type: 'singleSelect',
                selectableOptions: async (row: GridRowModel): Promise<GridIdNameModel[]> => {
                    const timeshift = row as TimeSheetItem;
                    const items = timeshift.timeshiftType === TimeSheetItemType.SHIFT
                        ? shiftTypes.items
                        : absenceTypes.items;

                    if (timeshift.timeshiftType === TimeSheetItemType.ABSENCE) {
                        return mapAbsenceTypeOptions(timeshift, absenceTypes);
                    }

                    return _.orderBy(items, (x) => x.default ? 0 : x.id).map((x) => ({
                        id: x.id,
                        name: x.name
                    }));
                }
            },
            {
                field: 'note',
                localizationKey: Lx.Schedule.TIMESHIFT_NOTE,
                flex: { grow: 1 },
                muted: true,
                editable: canEditRow,
                type: 'text'
            }
        ];

        if (includeTrackingColumns) {
            columns.push(
                {
                    field: 'plannedHours',
                    localizationKey: Lx.Schedule.PLANNED_HOURS_ABBREVIATION,
                    headerTooltipKey: Lx.Schedule.PLANNED_HOURS,
                    width: trackingColumnWidth,
                    displayValue: () => <HoursValue value={0} regularWeight />,
                    alignment: 'center',
                    visible: showTrackingColumns
                },
                {
                    field: 'workHours',
                    localizationKey: Lx.Schedule.WORKED_HOURS_ABBREVIATION,
                    headerTooltipKey: Lx.Schedule.WORKED_HOURS,
                    width: trackingColumnWidth,
                    displayValue: (_value, row) => {
                        const { workHours } = getMetrics(row as TimeSheetItem);
                        return <HoursValue value={workHours} regularWeight />;
                    },
                    alignment: 'center',
                    visible: showTrackingColumns
                },
                {
                    field: 'absenceHours',
                    localizationKey: Lx.Schedule.ABSENCE_HOURS_ABBREVIATION,
                    headerTooltipKey: Lx.Schedule.ABSENCE_HOURS,
                    width: trackingColumnWidth,
                    displayValue: (_value, row) => {
                        const { absenceHours } = getMetrics(row as TimeSheetItem);
                        return <HoursValue value={absenceHours} type='absence' />;
                    },
                    alignment: 'center',
                    visible: showTrackingColumns
                }
            );
        }

        if(!includeTrackingColumns)
            columns.push(
                {
                    field: 'internalNote',
                    localizationKey: Lx.Schedule.TIMESHIFT_INTERNAL_NOTE,
                    flex: { grow: 1 },
                    muted: true,
                    displayValue: (value) => canViewInternalNote ? renderSingleLineValue(String(value ?? '')) : '',
                    editable: (row: GridRowModel) => canManageInternalNote && canEditRow(row),
                    type: 'text'
                }
            );

        if (includeApprovalColumns) {
            columns.push(
                {
                    field: 'approvedHours',
                    localizationKey: Lx.Schedule.APPROVED_HOURS,
                    width: approvedHoursWidth,
                    displayValue: (_value, row) => {
                        const item = row as TimeSheetItem;
                        const { approvedHours } = getMetrics(item);
                        return (
                            <HoursValue
                                value={approvedHours}
                                total={item.hours}
                                type='approve'
                                mutedZero
                            />
                        );
                    },
                    alignment: 'center'
                },
                {
                    field: 'billableHours',
                    localizationKey: Lx.Schedule.BILLABLE_HOURS,
                    width: billableHoursWidth,
                    displayValue: (_value, row) => {
                        const item = row as TimeSheetItem;
                        const { billableHours } = getMetrics(item);
                        const total = item.timeshiftType === TimeSheetItemType.SHIFT ? item.hours : 0;
                        return (
                            <HoursValue
                                value={billableHours}
                                total={total}
                                type='billable'
                                mutedZero
                            />
                        );
                    },
                    alignment: 'center'
                },
            );
        }

        columns.push({
            field: 'totalHours',
            localizationKey: Lx.Schedule.TOTAL_HOURS,
            width: totalColumnWidth,
            cellClassName: styles.totalHoursCell,
            editable: canEditRow,
            type: 'number',
            min: 0.5,
            max: 24,
            step: 0.5,
            value: (row: GridRowModel) => (row as TimeSheetItem).hours,
            cellPrefix: (row) => {
                const item = row as TimeSheetItem;
                const timeBankLabel = getTimeBankLabel(item.timeBankHours);

                return timeBankLabel ? <TimeBankCellStripe label={timeBankLabel} /> : null;
            },
            cellTitle: (row) => {
                const item = row as TimeSheetItem;
                const timeBankLabel = getTimeBankLabel(item.timeBankHours);

                return timeBankLabel ? getTimeBankCellTitle(timeBankLabel) : undefined;
            },
            displayValue: (_value, row) => {
                const item = row as TimeSheetItem;
                return <HoursValue value={item.hours} />;
            },
            alignment: 'end'
        });

        columns.push(
            {
                field: 'approveAction',
                width: actionColumnWidth,
                hideHeaderContent: true,
                cellRender: (_field, row, intl, cellDisabled) => buildApproveButton(row as TimeSheetItem, intl, disabled || cellDisabled),
                alignment: 'center'
            },
            {
                field: 'billableAction',
                width: actionColumnWidth,
                hideHeaderContent: true,
                cellRender: (_field, row, intl, cellDisabled) => buildBillableButton(row as TimeSheetItem, intl, disabled || cellDisabled),
                alignment: 'center'
            },
            {
                field: 'actions',
                width: actionColumnWidth,
                hideHeaderContent: true,
                cellRender: (_field, row, intl, cellDisabled) => buildActionMenu(row as TimeSheetItem, intl, disabled || cellDisabled),
                alignment: 'center'
            }
        );

        return columns;
    }, [
        absenceTypes.items,
        approveAbility,
        billalbeAbility,
        canShowApproveAction,
        canShowBillableAction,
        canManageEntry,
        canManageInternalNote,
        canViewInternalNote,
        getShiftEmployee,
        groupType,
        includeTrackingColumns,
        includeApprovalColumns,
        disabled,
        onAddShiftClick,
        onApproveClick,
        onBillClick,
        onDeleteItemClick,
        onEditItemClick,
        onCopyItemClick,
        projects,
        shiftTypes.items,
        compactDesktop,
        compactTextColumns,
        dateColumnWidth,
        detailsColumnWidth,
        textDetailsColumnWidth,
        trackingColumnWidth,
        actionColumnWidth,
        approvedHoursWidth,
        billableHoursWidth,
        totalColumnWidth,
        minEditableDate,
        maxEditableDate
    ]);
};

export default useGridColumns;
